import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SideBarMenuItem, ISidebarMenuItem, onActiveLink } from '../sidebar-menu-item/SideBarMenuItem';
import { getMenuItems } from '../api/api';
import { ROUTER_PATHS as ROUTER_PATHS_USERCARE } from '../../../user-care/constant/constant';
import { ROUTER_PATHS as ROUTER_PATHS_DISTRIBUTION } from '../../../distribution/constant/router-contant';
import { ReactComponent as OverviewIcon } from "../../assets/Overview.svg";
import { ReactComponent as DownloadIcon } from "../../assets/Download.svg";
import {ReactComponent as Subscription } from "../../assets/Manage-Subscriptions.svg";
import {ReactComponent as Distribution } from "../../assets/Manage-Distribution.svg";
import { ReactComponent as UserCare } from "../../assets/Manage-Configurations.svg";
import { ReactComponent as Support } from "../../assets/News-Updates.svg";
import {ReactComponent as MyAccount  } from "../../assets/Activity-Log.svg"
import { ReactComponent as Resources } from "../../assets/Resources.svg";
import { ENDPOINTS } from '../../../subscriptions/api/end-point';
import { commonPostServiceApi } from '../../../../services/api-service';
import { getViewReports, type ReportItem } from '../../../user-care/api/reports-api';
import { GetPrimusEliteOptionsMenu } from '../../../my-account/api/api';
import { useDssContext } from '../../../../DSSContext';
import DSSLoader from '../../../../ui/building-blocks/atoms/dss-loader/dss-loader';
import { IDENTIFIER_ROUTE_MAP, SUBSCRIPTION_CHILD_KEYS, ASSOCIATED_ROUTES } from './sidebar-user-routes';
import { INDS_CUSTOMER, INDS_OP_ADMIN } from '../../../../util/roles';
import { USER_GUIDE_EXTERNAL_URL, USER_GUIDE_INTERNAL_URL } from '../../../inds-pre-login-screen/constant/api-constants';

type Props = {
  active: string;
  onMenuClick: () => void;
  openSubMenu: string | null;
  setOpenSubMenu: (key: string | null) => void;
  ['data-testid']?: string;
  menuPosition: any;
  refreshCounts?: React.MutableRefObject<() => void>;
  leftmenu: boolean;
  roleIds?: number[];
};

type ApiMenu = {
  menuId: number;
  menuIdentifier: string;
  menuTitle: string;
  menuLevel: number;
  parentMenuId: number | null;
  sortOrder: number;
  templateName: string;
  route: string | null;
  child: ApiMenu[] | null;
};

// routes map moved to sidebar-user-routes.ts

const normalizeTitle = (s: string) => s.trim().toUpperCase().replace(/\s+/g, ' ');

const filterPrimusFromItems = (
  items: ISidebarMenuItem[],
  hidePrimus: boolean,
): ISidebarMenuItem[] => {
  return items
    .filter((item) => !(hidePrimus && normalizeTitle(item.title || '') === 'PRIMUS ELITE OPTIONS'))
    .map((item) => ({
      ...item,
      subMenus: item.subMenus ? filterPrimusFromItems(item.subMenus, hidePrimus) : item.subMenus,
    }));
};

const toSidebarItems = (
  menus: ApiMenu[],
  navigate: (path: string, opts?: any) => void,
  leftmenu: boolean,
  pathname: string,
  subscriptionCount: any,
  reportsList: ReportItem[],
  loadingReports: boolean,
  primusEliteEnabled: boolean | null,
  roleIds: number[]
): ISidebarMenuItem[] => {
  const iconFor = (titleKey: string) => {
    switch (titleKey) {
      case 'OVERVIEW':
        return OverviewIcon;
      case 'SUPPORT':
        return Support;
      case 'FLIGHT INFO':
        return Support;
      case 'MY ACCOUNT':
        return MyAccount;
      case 'DATABASES':
        return DownloadIcon;
      case 'SUBSCRIPTIONS':
        return Subscription;
      case 'USER CARE':
        return UserCare;
      case 'DISTRIBUTION':
        return Distribution;
      case 'USER GUIDE & TUTORIALS':
        return Resources;
      default:
        return undefined;
    }
  };

  const buildItem = (m: ApiMenu, level: number): ISidebarMenuItem => {
    const child = Array.isArray(m.child) ? m.child : [];
    const idKey = (m.menuIdentifier || '').toUpperCase();
    const titleKey = normalizeTitle(m.menuTitle || '');
    // Prefer identifier-based routing; only fall back to title/explicit route
    let route = IDENTIFIER_ROUTE_MAP[idKey] || (m.route || '');
    if (idKey === 'USER_GUIDE_MEDIA') {
      const isCustomerOrOpAdmin = roleIds.includes(INDS_CUSTOMER) || roleIds.includes(INDS_OP_ADMIN);
      route = isCustomerOrOpAdmin
        ? USER_GUIDE_EXTERNAL_URL
        : USER_GUIDE_INTERNAL_URL;
    }
    // Special case: if top-level Databases has no children, route to My Downloads
    if (idKey === 'INDS_DATABASES' && (!Array.isArray(m.child) || m.child.length === 0)) {
      route = IDENTIFIER_ROUTE_MAP['MY_DOWNLOADS'];
    }
    const isTopLevelSubscriptions = (idKey === 'INDS_SUBSCRIPTIONS' || idKey === 'SUBSCRIPTIONS') && (m.parentMenuId === null || m.menuLevel === 1);
    const isContainer = child.length > 0 || isTopLevelSubscriptions;
    let onClick: (() => void) | undefined = undefined;
    const isHttp = typeof route === 'string' && /^(https?:)\/\//i.test(route);
    if (route && !isContainer) {
      onClick = isHttp ? () => window.open(route, '_blank', 'noopener,noreferrer') : () => navigate(route);
    } else {
      // Fallback for purchase link when backend doesn't provide a route
      const isPurchase = titleKey.includes('PURCHASE') && (titleKey.includes('INDS') || titleKey.includes('SERVICE'));
      if (isPurchase) {
        onClick = () => window.open('https://shop.jeppesen.com/INDS', '_blank', 'noopener,noreferrer');
      }
    }

    if (isContainer) {
      route = '';
    }
    const activeStr = route && !isHttp ? onActiveLink(pathname.indexOf(route) > -1) : '';

    const routeFromIdentifiers = (c: ApiMenu): string | undefined => {
      const cId = (c.menuIdentifier || '').toUpperCase();
      const cTitle = normalizeTitle(c.menuTitle || '');
      return IDENTIFIER_ROUTE_MAP[cId] || c.route || undefined;
    };

    const ensureSubscriptionsChildren = (children: ApiMenu[]): ISidebarMenuItem[] => {
      const required = [
        { key: 'ADD_PART2', fallbackTitle: 'Add Part 2', route: '/userlogin/subscription/addpart2', countKey: 'addPart2SubCount', badgeClass: 'part2-badge' },
        { key: 'MODIFY', fallbackTitle: 'Modify', route: '/userlogin/subscription/modify', countKey: 'modifiedSubsCount', badgeClass: 'modify-badge' },
        { key: 'EXCEPTIONS', fallbackTitle: 'Exceptions', route: '/userlogin/subscription/exception', countKey: 'errorSubCount', badgeClass: 'exception-badge' },
        { key: 'ACTIVE', fallbackTitle: 'Active', route: '/userlogin/subscription/active', countKey: 'activeSubCount', badgeClass: 'active-badge' },
        { key: 'EXPIRED', fallbackTitle: 'Expired', route: '/userlogin/subscription/expire', countKey: 'expiredCount', badgeClass: 'error-badge' },
        // { key: 'SEARCH_SUBSCRIPTION', fallbackTitle: 'Search Subscription', route: '/userlogin/subscription/globalSearch' },
      ];

      const childList = Array.isArray(children) ? children : [];
      const ensureItem = (req: typeof required[number]): ISidebarMenuItem => {
        const found = childList.find(c => {
          const cId = (c.menuIdentifier || '').toUpperCase();
          const cTitle = normalizeTitle(c.menuTitle || '');
          const cRoute = routeFromIdentifiers(c);
          return cId === req.key || cTitle === normalizeTitle(req.fallbackTitle) || cRoute === req.route;
        });
        const title = found ? found.menuTitle : req.fallbackTitle;
        const childRoute = found ? (routeFromIdentifiers(found) || req.route) : req.route;
        const isHttpChild = typeof childRoute === 'string' && /^(https?:)\/\//i.test(childRoute);
        const onClickChild = isHttpChild ? () => window.open(childRoute!, '_blank', 'noopener,noreferrer') : () => navigate(childRoute!);
        const countVal = req.countKey ? subscriptionCount?.[req.countKey] : undefined;
        return {
          title,
          leftmenu,
          active: (!isHttpChild && childRoute) ? onActiveLink(pathname.indexOf(childRoute!) > -1) : '',
          onClick: onClickChild,
          count: typeof countVal === 'number' ? countVal : undefined,
          badgeClass: req.badgeClass,
        } as ISidebarMenuItem;
      };

      const orderedRequired = required.map(ensureItem);
      const extras = childList.filter(c => {
        const cId = (c.menuIdentifier || '').toUpperCase();
        const cTitle = normalizeTitle(c.menuTitle || '');
        return !required.some(req => req.key === cId || normalizeTitle(req.fallbackTitle) === cTitle);
      }).map(c => buildItem(c, 1));

      return [...orderedRequired, ...extras];
    };

    return {
      title: m.menuTitle,
      active: activeStr,
      hasIcon: level === 0,
      iconAssetPath: level === 0 ? iconFor(titleKey) : undefined,
      // do not set hasLeftBox; it causes dot markers
      leftmenu,
      onClick,
      subMenus:
        (() => {
          if (isTopLevelSubscriptions) {
            return ensureSubscriptionsChildren(child);
          }
          if (child.length > 0) {
            let mappedChildren = child
              .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
              .map((c) => buildItem(c, level + 1));

            // Inject dynamic Reports under User Care for parity with static sidebar
            const isUserCare = titleKey === 'USER CARE' || idKey === 'USER_CARE';
            if (isUserCare) {
              const baseReportsPath = `${ROUTER_PATHS_USERCARE.USER_CARE}/${ROUTER_PATHS_USERCARE.Reports.reports}`;
              const childHasReports = child.some((c) => {
                const cid = (c.menuIdentifier || '').toUpperCase();
                const ctitle = normalizeTitle(c.menuTitle || '');
                return cid === 'INDS_REPORTS' || ctitle === 'REPORTS';
              });
              const reportsChildren: ISidebarMenuItem[] = (() => {
                if (loadingReports && reportsList.length === 0 && childHasReports) {
                  return [{ leftmenu, title: 'Loading...', active: '', onClick: () => {} } as ISidebarMenuItem];
                }
                return [...reportsList]
                  .sort((a, b) => String(a.reportName || '').localeCompare(String(b.reportName || ''), undefined, { sensitivity: 'base' }))
                  .map((r) => ({
                  leftmenu,
                  title: r.reportName,
                  active: onActiveLink(pathname.indexOf(`${baseReportsPath}/${r.reportId}`) > -1),
                  onClick: () => navigate(`${baseReportsPath}/${r.reportId}`, { state: { title: r.reportName } }),
                } as ISidebarMenuItem));
              })();

              const reportsIsActive = reportsChildren.some(ci => ci.active === 'active') || onActiveLink(pathname.indexOf(baseReportsPath) > -1) === 'active';
              const reportsItem: ISidebarMenuItem = ({
                leftmenu,
                title: 'Reports',
                active: reportsIsActive ? 'active' : '',
                subMenus: reportsChildren,
              } as unknown) as ISidebarMenuItem;
              const shouldShowReports = childHasReports || reportsChildren.length > 0;
              if (shouldShowReports) {
                const filteredChildren = mappedChildren.filter((ci) => normalizeTitle(ci.title) !== 'REPORTS');
                // Place Reports right after Shipment Status Tracking when present
                const shipmentIdx = filteredChildren.findIndex(ci => normalizeTitle(ci.title) === 'SHIPMENT STATUS TRACKING');
                if (shipmentIdx >= 0) {
                  const before = filteredChildren.slice(0, shipmentIdx + 1);
                  const after = filteredChildren.slice(shipmentIdx + 1);
                  mappedChildren = [...before, reportsItem, ...after];
                } else {
                  mappedChildren = [...filteredChildren, reportsItem];
                }
              }
            }

            return mappedChildren;
          }
          return undefined;
        })(),
    } as ISidebarMenuItem;
  };

  return menus
    .slice()
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map((m) => buildItem(m, 0));
};

const SideBarUserDynamic: React.FC<Props> = (props) => {
  const nav = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [menuData, setMenuData] = useState<ApiMenu[]>([]);
  const { userDetail, selectedCustomer, setAllowedRoutes } = useDssContext();
    // const roleIds = props.roleIds || [22];

  const roleIds = useMemo<number[]>(() => {
    if (Array.isArray(props.roleIds) && props.roleIds.length) return props.roleIds as number[];
    const assoc: any[] = ((userDetail as any)?.userCustAssociation) || [];
    const firstRoleId = Number(assoc?.[0]?.roleId);
    if (!Number.isNaN(firstRoleId)) return [firstRoleId];
 
    return [];
  }, [props.roleIds, userDetail]);
  const [subscriptionCount, setSubscriptionCount] = useState<any>({});
  const [reportsList, setReportsList] = useState<ReportItem[]>([]);
  const [loadingReports, setLoadingReports] = useState<boolean>(false);
  const [primusEliteEnabled, setPrimusEliteEnabled] = useState<boolean | null>(null);

  const hasSubscriptionsMenu = useMemo(() => {
    if (!Array.isArray(menuData) || menuData.length === 0) return false;
    return menuData.some((n) => {
      const idKey = (n.menuIdentifier || '').toUpperCase();
      return idKey === 'INDS_SUBSCRIPTIONS';
    });
  }, [menuData]);

  const hasReportsMenu = useMemo(() => {
    const scan = (nodes: ApiMenu[]): boolean => {
      return nodes.some((n) => {
        const idKey = (n.menuIdentifier || '').toUpperCase();
        const titleKey = normalizeTitle(n.menuTitle || '');
        const children = Array.isArray(n.child) ? n.child : [];
        if (idKey === 'INDS_REPORTS' || titleKey === 'REPORTS') return true;
        return children.length > 0 ? scan(children) : false;
      });
    };
    return Array.isArray(menuData) && menuData.length > 0 ? scan(menuData) : false;
  }, [menuData]);

  const primusCustomerRefSeq = useMemo(() => {
    if (selectedCustomer) {
      const selectedCustomerDetails = (userDetail?.customers || []).find(
        (customer: any) => String(customer.customerRefSeq) === String(selectedCustomer)
      ) as any;
      return selectedCustomerDetails?.customerRefSeq ? Number(selectedCustomerDetails.customerRefSeq) : 0;
    }

    const firstCustomer = (userDetail?.customers || [])[0] as any;
    if ((userDetail?.customers || []).length === 1 && firstCustomer?.customerRefSeq) {
      return Number(firstCustomer.customerRefSeq);
    }

    return 0;
  }, [selectedCustomer, userDetail?.customers]);

  useEffect(() => {
    let mounted = true;
    if (!Array.isArray(roleIds) || roleIds.length === 0) {
      setMenuData([]);
      return () => {
        mounted = false;
      };
    }
    setLoading(true);
    getMenuItems(roleIds)
      .then((data) => {
        if (!mounted) return;
        setMenuData(Array.isArray(data) ? (data as ApiMenu[]) : []);
      })
      .catch(() => {
        setMenuData([]);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [roleIds.join(',')]);

  // Close all submenus when sidebar is closed (active becomes empty)
  useEffect(() => {
    if (!props.active || props.active === '') {
      props.setOpenSubMenu(null);
    }
  }, [props.active]);

  // Extract allowed routes from menu data and set in context
  useEffect(() => {
    if (!Array.isArray(menuData) || menuData.length === 0) {
      setAllowedRoutes([]);
      return;
    }
    const hidePrimus = (roleIds.includes(INDS_CUSTOMER) || roleIds.includes(INDS_OP_ADMIN)) && primusEliteEnabled !== true;
    const routes: string[] = ['/userlogin/overview'];
    const extractRoutes = (menus: ApiMenu[]) => {
      menus.forEach((m) => {
        const idKey = (m.menuIdentifier || '').toUpperCase();
        // Skip Primus Elite route when it should be hidden
        if (hidePrimus && idKey === 'PRIMUS_ELITE_OPTIONS') return;
        const hasChildren = Array.isArray(m.child) && m.child.length > 0;

        // Mirror the same special case as buildItem:
        // if INDS_DATABASES has no children, user only has admin-download access
        let route = IDENTIFIER_ROUTE_MAP[idKey] || m.route || '';
        if (idKey === 'INDS_DATABASES' && !hasChildren) {
          route = IDENTIFIER_ROUTE_MAP['MY_DOWNLOADS']; // /userlogin/database/admin-download
        }

        // Only add routes for leaf items (no children) to prevent over-permissive access.
        if (route && !route.startsWith('http') && !hasChildren) {
          routes.push(route);
        }

        // For subscriptions: children are created manually (not from API child array)
        // so explicitly add the module path and all known child routes
        if (idKey === 'INDS_SUBSCRIPTIONS' || idKey === 'SUBSCRIPTIONS') {
          routes.push('/userlogin/subscription');
          SUBSCRIPTION_CHILD_KEYS.forEach(k => {
            const r = IDENTIFIER_ROUTE_MAP[k];
            if (r) routes.push(r);
          });
        }

        const associated = ASSOCIATED_ROUTES[idKey];
        if (associated) {
          associated.forEach(r => routes.push(r));
        }

        if (hasChildren) {
          extractRoutes(m.child!);
        }
      });
    };
    extractRoutes(menuData);
    setAllowedRoutes([...new Set(routes)]);
  }, [menuData, primusEliteEnabled, roleIds]);

  // Single function to fetch subscription counts
  const fetchSubscriptionCounts = async () => {
    try {
      const result = await commonPostServiceApi<any>(ENDPOINTS.getAllSubscriptionCounts);
      setSubscriptionCount(result);
    } catch (err) {
    }
  };

  // Fetch counts initially and expose refresh function
  useEffect(() => {
    if (hasSubscriptionsMenu) {
      fetchSubscriptionCounts();
      
      // Expose refresh function via ref
      if (props.refreshCounts) {
        props.refreshCounts.current = fetchSubscriptionCounts;
      }
    }
  }, [hasSubscriptionsMenu]);

  useEffect(() => {
    if (!hasReportsMenu) {
      setReportsList([]);
      setLoadingReports(false);
      return;
    }

    let mounted = true;
    const fetchReports = async () => {
      try {
        setLoadingReports(true);
        const reports = await getViewReports(roleIds[0]);
        if (mounted) {
          setReportsList(Array.isArray(reports) ? reports : []);
        }
      } catch (err) {
        if (mounted) {
          setReportsList([]);
        }
      } finally {
        if (mounted) setLoadingReports(false);
      }
    };
    fetchReports();

    return () => {
      mounted = false;
    };
  }, [hasReportsMenu]);

  useEffect(() => {
    const hasPrimusRole = roleIds.includes(INDS_CUSTOMER) || roleIds.includes(INDS_OP_ADMIN);
    if (!hasPrimusRole) {
      setPrimusEliteEnabled(null);
      return;
    }

    if (!primusCustomerRefSeq) {
      setPrimusEliteEnabled(false);
      return;
    }

    setPrimusEliteEnabled(false);

    let mounted = true;
    
    (async () => {
      try {
        if (mounted) {
          const result = await GetPrimusEliteOptionsMenu({
            customerNumber: primusCustomerRefSeq,
          });
          if (mounted) {
            setPrimusEliteEnabled(result?.primusEliteOptionsEnabled === true);
          }
        }
      } catch (err) {
        if (mounted) {
          setPrimusEliteEnabled(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [roleIds.join(','), primusCustomerRefSeq]);

  const items = useMemo(
    () => toSidebarItems(
      menuData,
      nav,
      props.leftmenu,
      location.pathname.toString(),
      subscriptionCount,
      reportsList,
      loadingReports,
      primusEliteEnabled,
      roleIds
    ),
    [menuData, nav, props.leftmenu, location.pathname, subscriptionCount, reportsList, loadingReports, primusEliteEnabled, roleIds]
  );

  const visibleItems = useMemo(
    () => filterPrimusFromItems(items, (roleIds.includes(INDS_CUSTOMER) || roleIds.includes(INDS_OP_ADMIN)) && primusEliteEnabled !== true),
    [items, roleIds, primusEliteEnabled]
  );

  const overviewItem: ISidebarMenuItem = {
    title: 'Overview',
    hasIcon: true,
    iconAssetPath: OverviewIcon,
    leftmenu: props.leftmenu,
    active: onActiveLink(location.pathname.toString().indexOf('/overview') > -1),
    onClick: () => nav('/userlogin/overview'),
  } as ISidebarMenuItem;

  return (
    <div className={"sidebarmenu"} data-testid={props['data-testid'] || 'sidebar-user-dynamic'}>
      {loading && <DSSLoader id=''/>}
      {!loading && [overviewItem, ...visibleItems].map((it, idx) => (
        <SideBarMenuItem
          key={idx}
          {...it}
          menuKey={idx === 0 ? 'Overview' : `m_${idx}`}
          openSubMenu={props.openSubMenu}
          setOpenSubMenu={props.setOpenSubMenu}
        />
      ))}
    </div>
  );
};

export default SideBarUserDynamic;
